import sumBy from 'lodash/sumBy'
import { mapGetters, mapActions } from 'vuex'

const KITS_INFINITY = -1
const PAGEVIEWS_INFINITY = -1

export const KitSubscriptionMixin = {
  data() {
    return {
      // TODO: Metered Billing full release: remove
      isMeteredBillingPageviewsPartiallyReleased: false
    }
  },

  computed: {
    canCreateProKits() {
      return this.$permits.get('accessToKits', 'proAllowed')
    },

    hasInfiniteKits() {
      return this.$permits.get('accessToKits', 'kitLimit') === KITS_INFINITY
    },

    hasPageviewsUnlimited() {
      return this.meteredBilling?.maxMonthlyPageviews === PAGEVIEWS_INFINITY
    },

    hasSetPageviewsBillingLimit() {
      return this.$subscription?.meteredBilling.maxMonthlyPageviewsMoneyLimit > 0
    },

    isMeteredBillingPageviews() {
      return this.isMeteredBillingPageviewsFeatureOn && this.isMeteredBillingPageviewsCustomerEnabled
    },

    isMeteredBillingPageviewsCustomerEnabled() {
      return this.meteredBilling?.meteredPageviewsOn
    },

    isMeteredBillingPageviewsFeatureOn() {
      return FONTAWESOME.METERED_PAGEVIEWS_ACTIVE === 'yes'
    },

    kitLimit() {
      return this.$permits.get('accessToKits', 'kitLimit') || 0
    },

    kitPageviewsOverageLimit() {
      return this.isMeteredBillingPageviewsCustomerEnabled ? this.totalPageviewsUsageLimit - this.pageviewsPlanLimit : 0
    },

    pageviewsDollarLimit() {
      return this.meteredBilling?.maxMonthlyPageviewMoneyLimit > 0 ? this.meteredBilling?.maxMonthlyPageviewsMoneyLimit / 100 : 0
    },

    pageviewsPlanLimit() {
      return this.$subscription?.plan?.monthlyPageviews || 0
    },

    planHasAccessToPageviewMeteredBilling() {
      return this.$subscription?.plan?.meteredBillingPageviewsEnabled
    },

    overagePageviewIncrement() {
      return this.$subscription?.plan?.overagePageviewIncrement || 0
    },

    totalPageviewsUsageLimit() {
      return this.meteredBilling?.maxMonthlyPageviews
    }
  }
}

export const KitsStatusAndUsageMixin = {
  data() {
    return {
      hasFetchedKits: false,
      kitsFetchFailed: false
    }
  },

  computed: {
    ...mapGetters('kits', ['currentKits', 'pendingKits', 'activeKits']),

    ...mapGetters('meteredBilling', ['meteredBilling']),

    ...KitSubscriptionMixin.computed,

    kitCount() {
      // pending kits don't count towards the kit count
      return this.activeKits.length
    },

    doNotAllowAnyMoreKits() {
      if (this.hasPendingKit) {
        return true
      } else {
        return !this.hasInfiniteKits && this.kitCount >= this.kitLimit
      }
    },

    hasPendingKit() {
      return this.pendingKits.length > 0
    },

    kitsLoading() {
      return !this.hasFetchedKits
    },

    atPageviewsPlanLimit() {
      return this.totalPageviews >= this.pageviewsPlanLimit
    },

    totalPageviews() {
      return sumBy(this.currentKits, function (k) {
        return k.pageviews
      })
    },

    hasReachedPageviewsOverageLimit() {
      return this.isMeteredBillingPageviews && !this.hasPageviewsUnlimited && this.totalPageviews > this.totalPageviewsUsageLimit
    },

    kitUsagePercentage() {
      return (this.totalPageviews / this.pageviewsPlanLimit) * 100
    },

    kitPageviewsOverageUsage() {
      const overage = this.totalPageviews - (this.$subscription?.plan?.monthlyPageviews || 0)

      return Math.max(overage, 0)
    },

    kitPageviewsOverageUsagePercentage() {
      return (this.kitPageviewsOverageUsage / this.kitPageviewsOverageLimit) * 100
    },

    kitPageviewsOverageDangerPercentage() {
      return this.kitPageviewsOverageLimit ? 100 : this.$subscription?.plan?.monthlyPageviews * 10
    },

    kitsDeactivated() {
      return this.currentKits.some((k) => k.status === 'deactivated')
    },

    billablePageviewsOverage() {
      if (this.$subscription?.type === 'free') return false

      const planPageviewsAllowance = this.$subscription?.plan?.monthlyPageviews || 0

      const actualOveragePageviews = Math.max(this.totalPageviews - planPageviewsAllowance, 0)

      const overagePageviewIncrement = this.$subscription?.plan?.overagePageviewIncrement || 0

      const isOverageLessThanOneIncrement = actualOveragePageviews < overagePageviewIncrement

      if (isOverageLessThanOneIncrement) {
        // We don't bill for these small overages.
        return 0
      }

      if (this.isMeteredBillingPageviewsCustomerEnabled && this.hasPageviewsUnlimited) {
        return actualOveragePageviews
      }

      const isMeteredBillingLimitedWithExcess =
        this.isMeteredBillingPageviewsCustomerEnabled && this.totalPageviewsUsed >= this.meteredBilling?.maxMonthlyPageviews

      if (isMeteredBillingLimitedWithExcess) {
        // If metered billing is enabled, with a limit,
        // we cap billable overages at the max monthly limit set by the customer.
        return Math.max(this.meteredBilling?.maxMonthlyPageviews - planPageviewsAllowance, 0)
      }

      return actualOveragePageviews
    }
  },

  methods: {
    ...mapActions('kits', { fetchKitsFromStore: 'fetchKits' }),

    async fetchKits() {
      if (this.$account) {
        try {
          await this.fetchKitsFromStore()

          this.hasFetchedKits = true
        } catch (e) {
          this.kitsFetchFailed = true
        }
      }
    }
  }
}

export const DomainsMixin = {
  methods: {
    hasDomains(kit) {
      return kit.domains.indexOf('*.*') === -1
    },

    numberOfDomainsLabel(kit) {
      const num = kit.domains.length

      return `${num} ${num === 1 ? 'domain' : 'domains'}`
    }
  }
}
